import { Link } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";

export default function AboutPage() {
  return (
    <PublicLayout>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* ── HERO ── */}
      <section className="bg-slate-900 pt-36 pb-24 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle at 30% 60%, rgba(249,115,22,0.5) 0%, transparent 60%)" }}
        />
        <div className="relative max-w-4xl mx-auto px-5 text-center">
          <p className="text-orange-400 font-semibold text-sm uppercase tracking-widest mb-4">About TumaTrack</p>
          <h1
            className="text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            Delivery visibility for{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
              every Kenyan business
            </span>
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed">
            TumaTrack started with one observation: most Kenyan delivery operations
            run on WhatsApp messages, paper logs, and guesswork. We built the tool
            we wished existed.
          </p>
        </div>
      </section>

      {/* ── MISSION ── */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-orange-500 font-semibold text-sm uppercase tracking-widest mb-3">Our Mission</p>
              <h2
                className="text-4xl font-bold text-slate-900 mb-5 leading-tight"
                style={{ fontFamily: "'Sora', sans-serif" }}
              >
                Bring enterprise logistics to every business size
              </h2>
              <p className="text-slate-500 leading-relaxed mb-4">
                Whether you run a small pharmacy with one boda boda, or a distribution
                company with a fleet of lorries — you deserve real-time visibility
                into your deliveries.
              </p>
              <p className="text-slate-500 leading-relaxed">
                TumaTrack is priced and designed for the Kenyan market. M-Pesa billing,
                Kenya Shilling pricing, and built to work on the networks and devices
                your team already uses.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: "🏪", label: "Any business type",  desc: "Shops, pharmacies, supermarkets, couriers" },
                { icon: "🚗", label: "Any vehicle",         desc: "Motorcycle, bicycle, van, truck, bus, lorry" },
                { icon: "📱", label: "Driver mobile app",   desc: "Mobile app — works on Android" },
                { icon: "🌍", label: "Built for Kenya",     desc: "M-Pesa billing, KES pricing, local support" },
              ].map((item) => (
                <div key={item.label} className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                  <span className="text-3xl block mb-3">{item.icon}</span>
                  <p className="font-bold text-slate-900 text-sm mb-1" style={{ fontFamily: "'Sora', sans-serif" }}>
                    {item.label}
                  </p>
                  <p className="text-slate-500 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── THE PLATFORM ── */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-5xl mx-auto px-5">
          <div className="text-center mb-14">
            <p className="text-orange-500 font-semibold text-sm uppercase tracking-widest mb-3">The Platform</p>
            <h2 className="text-4xl font-bold text-slate-900" style={{ fontFamily: "'Sora', sans-serif" }}>
              Reliable tech, local focus
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: "⚡",
                title: "Real-time by default",
                desc: "Every status update and location ping appears instantly on your dashboard. Your dispatch team sees changes as they happen.",
              },
              {
                icon: "🔒",
                title: "Multi-tenant & secure",
                desc: "Every shop's data is fully isolated and protected. One platform, zero data crossover between clients.",
              },
              {
                icon: "🗺️",
                title: "Open maps stack",
                desc: "We use open-source maps so there are no hidden map fees that grow with your order volume.",
              },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm">
                <div className="text-3xl mb-4">{item.icon}</div>
                <h3 className="font-bold text-slate-900 text-lg mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>
                  {item.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-white border-t border-slate-100">
        <div className="max-w-2xl mx-auto px-5 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>
            Try TumaTrack free for 7 days
          </h2>
          <p className="text-slate-500 mb-8">No credit card. No commitment. Just better deliveries.</p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-7 py-3.5 rounded-xl shadow-md shadow-orange-200 hover:shadow-orange-300 transition-all duration-200 hover:-translate-y-0.5"
          >
            Get Started Free
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}